import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ti } from "@/lib/api";
import { toast } from "sonner";
import {
  Server, UserPlus, Package, FileSignature, Bell, Shield, ArrowLeft, LogOut,
  RefreshCw, Search, ClipboardList,
} from "lucide-react";

export default function TI() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("fila");
  const [dash, setDash] = useState({});
  const [fila, setFila] = useState([]);
  const [provs, setProvs] = useState([]);
  const [ativos, setAtivos] = useState([]);
  const [comodatos, setComodatos] = useState([]);
  const [cpfBusca, setCpfBusca] = useState("");
  const [busca, setBusca] = useState(null);
  const [novoAtivo, setNovoAtivo] = useState({ tipo: "Notebook", numero_serie: "", marca: "", modelo: "", numero_patrimonio: "", observacoes: "" });

  useEffect(() => { load(); }, [tab]);
  const load = async () => {
    try {
      setDash((await ti.dashboard()).data);
      if (tab === "fila") setFila((await ti.fila()).data);
      if (tab === "provisionados") setProvs((await ti.provisionados()).data);
      if (tab === "ativos") setAtivos((await ti.ativos()).data);
      if (tab === "comodatos") setComodatos((await ti.comodatos()).data);
    } catch { toast.error("Erro"); }
  };

  const buscarCol = async () => {
    if (!cpfBusca) return toast.error("Informe o CPF");
    try {
      const r = await ti.buscarColaborador(cpfBusca);
      setBusca(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Não encontrado"); setBusca(null); }
  };

  const provisionar = async () => {
    try {
      await ti.criarIdentidade({ cpf: cpfBusca, setor: "operacoes", nivel: "operacional" });
      toast.success("Identidade digital criada");
      setBusca(null); setCpfBusca("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const cadastrarAtivo = async () => {
    if (!novoAtivo.numero_serie) return toast.error("Nº série obrigatório");
    try {
      await ti.criarAtivo(novoAtivo);
      toast.success("Ativo cadastrado");
      setNovoAtivo({ tipo: "Notebook", numero_serie: "", marca: "", modelo: "", numero_patrimonio: "", observacoes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const items = [
    { id: "fila", label: "Fila de Provisionamento", icon: ClipboardList, badge: dash.fila_provisionamento },
    { id: "identidade", label: "Criar Identidade Digital", icon: UserPlus },
    { id: "provisionados", label: "Já Provisionados", icon: Shield, badge: dash.provisionados },
  ];
  const ativosItems = [
    { id: "ativos", label: "Inventário de Ativos", icon: Package },
    { id: "comodatos", label: "Termos de Comodato", icon: FileSignature, badge: dash.comodatos_ativos },
  ];

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <header className="bg-gradient-to-r from-[#1A1D42] to-[#252952] border-b border-[#B28F4B]/30">
        <div className="max-w-[1600px] mx-auto px-5 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#0A0B1A] border-2 border-[#B28F4B] flex items-center justify-center">
            <Server size={18} className="text-[#B28F4B]"/>
          </div>
          <div className="flex-1">
            <div className="text-[#B28F4B] font-bold font-heading text-lg">GRUPO SOEASY</div>
            <div className="text-white/60 text-[10px] uppercase tracking-widest">TI · Onboarding Digital · Fase 3</div>
          </div>
          <span className="px-3 py-1 rounded-md bg-[#B28F4B]/20 border border-[#B28F4B]/40 text-[#D4AF37] text-[10px] font-bold uppercase tracking-widest">🔒 LGPD Ativo</span>
          <button className="px-3 py-1.5 rounded-md border border-white/20 text-white text-xs font-bold uppercase tracking-widest flex items-center gap-1"><Bell size={12}/> Notificações {dash.revogacoes_pendentes > 0 && <span className="ml-1 px-1.5 rounded bg-red-500 text-white">{dash.revogacoes_pendentes}</span>}</button>
          <button onClick={() => navigate("/setores")} data-testid="btn-back-setores" className="px-3 py-1.5 rounded-md border border-[#B28F4B]/40 text-white text-xs font-bold uppercase tracking-widest">Setores</button>
          <button onClick={() => { logout(); navigate("/login"); }} className="px-3 py-1.5 rounded-md bg-red-500/20 border border-red-500/40 text-red-300 text-xs font-bold uppercase tracking-widest">✕ Sair</button>
        </div>
      </header>

      <div className="flex-1 flex">
        <aside className="w-64 bg-[#0A0B1A] border-r border-[#B28F4B]/20 shrink-0 max-h-[calc(100vh-56px)] overflow-y-auto sticky top-0">
          <div className="p-5 border-b border-[#B28F4B]/20">
            <div className="text-[#B28F4B] text-base font-bold font-heading">PROVISIONAMENTO TI</div>
            <div className="text-white/50 text-[10px] uppercase tracking-widest">Onboarding Digital — Fase 3</div>
          </div>
          <div className="p-3">
            <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-2 px-2">✦ Onboarding</div>
            {items.map(it => (
              <button key={it.id} onClick={() => setTab(it.id)} data-testid={`ti-nav-${it.id}`}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg mb-1 border ${tab===it.id?"bg-[#1F2350] border-[#B28F4B]":"bg-transparent border-transparent hover:bg-[#131532]/60"}`}>
                <div className="flex items-center gap-2"><it.icon size={13} className={tab===it.id?"text-[#B28F4B]":"text-white/70"}/><span className={`text-[11px] font-bold uppercase tracking-wide ${tab===it.id?"text-[#B28F4B]":"text-white"}`}>{it.label}</span></div>
                {it.badge > 0 && <span className="px-1.5 rounded-full bg-red-500 text-white text-[9px] font-bold">{it.badge}</span>}
              </button>
            ))}
          </div>
          <div className="p-3 border-t border-[#B28F4B]/15">
            <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-2 px-2">✦ Ativos</div>
            {ativosItems.map(it => (
              <button key={it.id} onClick={() => setTab(it.id)} data-testid={`ti-nav-${it.id}`}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg mb-1 border ${tab===it.id?"bg-[#1F2350] border-[#B28F4B]":"bg-transparent border-transparent hover:bg-[#131532]/60"}`}>
                <div className="flex items-center gap-2"><it.icon size={13} className={tab===it.id?"text-[#B28F4B]":"text-white/70"}/><span className={`text-[11px] font-bold uppercase tracking-wide ${tab===it.id?"text-[#B28F4B]":"text-white"}`}>{it.label}</span></div>
                {it.badge > 0 && <span className="px-1.5 rounded-full bg-[#B28F4B] text-[#1A1D42] text-[9px] font-bold">{it.badge}</span>}
              </button>
            ))}
          </div>
          <div className="p-4 border-t border-[#B28F4B]/15">
            <div className="text-[#B28F4B] text-[10px] font-bold uppercase tracking-[0.25em] mb-2">✦ Resumo</div>
            {[
              ["Aguardando TI", dash.fila_provisionamento, "#F59E0B"],
              ["Provisionados", dash.provisionados, "#10B981"],
              ["Ativos livres", dash.ativos_livres, "#22D3EE"],
              ["Comodatos ativos", dash.comodatos_ativos, "#B28F4B"],
            ].map(([l, v, c]) => (
              <div key={l} className="flex justify-between text-[10px] text-white/60 mb-1"><span>{l}</span><span className="font-mono font-bold" style={{color: c}}>{v ?? 0}</span></div>
            ))}
          </div>
          <div className="p-4 text-center text-[#6B6E8F] text-[9px] font-mono uppercase tracking-widest">SOEASY TI v1.0 · 2026</div>
        </aside>

        <main className="flex-1 min-w-0 p-5">
          <div className="max-w-[1300px] mx-auto space-y-4">
            {tab === "fila" && (
              <>
                <div className="bg-[#1A1D42] border border-[#B28F4B]/30 rounded-2xl p-4 flex gap-3 items-start">
                  <Shield size={20} className="text-[#B28F4B] shrink-0 mt-0.5"/>
                  <div>
                    <div className="text-[#B28F4B] font-bold text-sm uppercase tracking-widest mb-1">🔒 Filtro LGPD — Visão Restrita ao Módulo TI</div>
                    <div className="text-white/80 text-xs">Campos visíveis: <span className="text-[#B28F4B]">Nome · CPF · Cargo · Filial · Contrato · Admissão</span></div>
                    <div className="text-white/60 text-xs mt-1">Bloqueados ao TI: Dados Bancários · Documentos (RG/CTPS/CNH) · ASO · Filiação · PIX</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <TiKpi label="Aguardando TI" value={dash.fila_provisionamento ?? 0} color="#F59E0B" emoji="⌛"/>
                  <TiKpi label="Ativos Livres" value={dash.ativos_livres ?? 0} color="#22D3EE" emoji="🖥️"/>
                  <TiKpi label="Provisionados" value={dash.provisionados ?? 0} color="#10B981" emoji="✅"/>
                  <TiKpi label="Comodatos Ativos" value={dash.comodatos_ativos ?? 0} color="#B28F4B" emoji="📋"/>
                </div>
                <div className="bg-white border border-[#B28F4B]/15 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-[#1A1D42] font-bold text-sm">👥 Colaboradores — Fila de Onboarding TI</div>
                    <input placeholder="Buscar nome ou CPF..." className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs w-56"/>
                  </div>
                  {fila.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-3xl mb-2">🎉</div>
                      <div className="text-slate-500 text-sm">Todos os colaboradores já foram provisionados!</div>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="p-2">Nome</th><th className="p-2">CPF</th><th className="p-2">Cargo</th><th className="p-2">Empresa</th><th className="p-2">Admissão</th></tr></thead>
                      <tbody>
                        {fila.map(f => (
                          <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="p-2 font-bold text-slate-800">{f.nome_completo}</td>
                            <td className="p-2 text-slate-600 font-mono">{f.cpf}</td>
                            <td className="p-2 text-slate-600">{f.cargo}</td>
                            <td className="p-2"><span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-bold">{f.empresa_nome}</span></td>
                            <td className="p-2 text-slate-500 text-xs font-mono">{f.data_admissao}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {tab === "identidade" && (
              <div className="bg-white border border-[#B28F4B]/15 rounded-2xl p-6">
                <div className="text-[#1A1D42] font-bold text-lg mb-1">🔍 Buscar Colaborador</div>
                <div className="text-slate-500 text-xs mb-4">Informe o CPF para carregar os dados LGPD-permitidos e gerar a sugestão de identidade</div>
                <div className="flex gap-2 mb-4">
                  <input placeholder="000.000.000-00" value={cpfBusca} onChange={e=>setCpfBusca(e.target.value)} data-testid="input-cpf-buscar" className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"/>
                  <button onClick={buscarCol} data-testid="btn-buscar-colab" className="px-5 py-2 rounded-lg bg-[#1A1D42] hover:bg-[#252952] text-white font-bold text-xs uppercase tracking-widest flex items-center gap-2"><Search size={13}/> Buscar</button>
                </div>
                {busca && (
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="text-[#1A1D42] font-bold text-sm mb-2">✓ Colaborador Encontrado</div>
                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                      <div><strong>Nome:</strong> {busca.colaborador.nome_completo}</div>
                      <div><strong>CPF:</strong> {busca.colaborador.cpf}</div>
                      <div><strong>Cargo:</strong> {busca.colaborador.cargo}</div>
                      <div><strong>Empresa:</strong> {busca.colaborador.empresa_nome}</div>
                      <div className="col-span-2"><strong>E-mail Corporativo Sugerido:</strong> <span className="text-[#B28F4B] font-mono font-bold">{busca.email_sugerido}</span></div>
                    </div>
                    {busca.ja_provisionado ? (
                      <div className="text-orange-600 text-xs font-bold">⚠️ Este colaborador já possui identidade digital ativa.</div>
                    ) : (
                      <button onClick={provisionar} data-testid="btn-provisionar" className="px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs uppercase tracking-widest">✓ Criar Identidade Digital</button>
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === "provisionados" && (
              <div className="bg-white border border-[#B28F4B]/15 rounded-2xl p-5">
                <div className="text-[#1A1D42] font-bold text-sm mb-4">✅ Colaboradores Provisionados</div>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="p-2">Nome</th><th className="p-2">CPF</th><th className="p-2">Cargo</th><th className="p-2">E-mail Corp.</th><th className="p-2">Setor</th><th className="p-2">Nível</th></tr></thead>
                  <tbody>
                    {provs.map(p => (
                      <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-2 font-bold text-slate-800">{p.nome}</td>
                        <td className="p-2 text-slate-600 font-mono">{p.cpf}</td>
                        <td className="p-2 text-slate-600">{p.cargo}</td>
                        <td className="p-2 text-[#B28F4B] font-mono">{p.email}</td>
                        <td className="p-2"><span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-bold uppercase">{p.setor}</span></td>
                        <td className="p-2 text-slate-600">{p.nivel}</td>
                      </tr>
                    ))}
                    {provs.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400 text-xs">Nenhum colaborador provisionado.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "ativos" && (
              <>
                <div className="bg-white border border-[#B28F4B]/15 rounded-2xl p-5">
                  <div className="text-[#1A1D42] font-bold text-sm mb-4">➕ Cadastrar Ativo</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <select value={novoAtivo.tipo} onChange={e => setNovoAtivo({...novoAtivo, tipo: e.target.value})} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
                      {["Notebook","Desktop","Monitor","Celular","Tablet","Impressora","Headset","Teclado","Mouse","Roteador","Servidor","Outro"].map(t => <option key={t}>{t}</option>)}
                    </select>
                    <input placeholder="Nº Série" value={novoAtivo.numero_serie} onChange={e => setNovoAtivo({...novoAtivo, numero_serie: e.target.value})} className="px-3 py-2 rounded-lg border border-slate-200 text-sm"/>
                    <input placeholder="Marca" value={novoAtivo.marca} onChange={e => setNovoAtivo({...novoAtivo, marca: e.target.value})} className="px-3 py-2 rounded-lg border border-slate-200 text-sm"/>
                    <input placeholder="Modelo" value={novoAtivo.modelo} onChange={e => setNovoAtivo({...novoAtivo, modelo: e.target.value})} className="px-3 py-2 rounded-lg border border-slate-200 text-sm"/>
                    <input placeholder="Nº Patrimônio" value={novoAtivo.numero_patrimonio} onChange={e => setNovoAtivo({...novoAtivo, numero_patrimonio: e.target.value})} className="px-3 py-2 rounded-lg border border-slate-200 text-sm col-span-1"/>
                    <input placeholder="Observações" value={novoAtivo.observacoes} onChange={e => setNovoAtivo({...novoAtivo, observacoes: e.target.value})} className="px-3 py-2 rounded-lg border border-slate-200 text-sm col-span-3"/>
                  </div>
                  <button onClick={cadastrarAtivo} data-testid="btn-cadastrar-ativo" className="px-5 py-2 rounded-lg bg-[#1A1D42] text-white font-bold text-xs uppercase tracking-widest">💾 Cadastrar Ativo</button>
                </div>
                <div className="bg-white border border-[#B28F4B]/15 rounded-2xl p-5">
                  <div className="text-[#1A1D42] font-bold text-sm mb-4">📋 Inventário ({ativos.length})</div>
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="p-2">Tipo</th><th className="p-2">Nº Série</th><th className="p-2">Marca</th><th className="p-2">Modelo</th><th className="p-2">Patrimônio</th><th className="p-2">Status</th></tr></thead>
                    <tbody>
                      {ativos.map(a => (
                        <tr key={a.id} className="border-b border-slate-100"><td className="p-2 font-bold">{a.tipo}</td><td className="p-2 font-mono">{a.numero_serie}</td><td className="p-2">{a.marca}</td><td className="p-2">{a.modelo}</td><td className="p-2 font-mono">{a.numero_patrimonio}</td><td className="p-2">{a.em_comodato ? <span className="text-orange-500 text-xs font-bold">EM COMODATO</span> : <span className="text-emerald-500 text-xs font-bold">LIVRE</span>}</td></tr>
                      ))}
                      {ativos.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400 text-xs">Nenhum ativo cadastrado.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {tab === "comodatos" && (
              <div className="bg-white border border-[#B28F4B]/15 rounded-2xl p-5">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-[#1A1D42] font-bold text-sm">📋 Termos de Comodato</div>
                  <input placeholder="Buscar CPF ou nome..." className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs w-56"/>
                </div>
                {comodatos.length === 0 ? (
                  <div className="text-center py-16"><div className="text-4xl mb-2">📋</div><div className="text-slate-400 text-sm">Nenhum comodato ativo.</div></div>
                ) : (
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 border-b border-slate-200"><th className="p-2">Colaborador</th><th className="p-2">CPF</th><th className="p-2">Ativo</th><th className="p-2">Nº Série</th><th className="p-2">Entrega</th><th className="p-2">Status</th></tr></thead>
                    <tbody>
                      {comodatos.map(c => (
                        <tr key={c.id} className="border-b border-slate-100"><td className="p-2 font-bold">{c.colaborador_nome}</td><td className="p-2 font-mono">{c.colaborador_cpf}</td><td className="p-2">{c.ativo_tipo}</td><td className="p-2 font-mono">{c.ativo_serie}</td><td className="p-2 font-mono">{c.data_entrega}</td><td className="p-2"><span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase">{c.status}</span></td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function TiKpi({ label, value, color, emoji }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
      <div className="text-3xl">{emoji}</div>
      <div>
        <div className="font-mono font-bold text-2xl" style={{color}}>{value}</div>
        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      </div>
    </div>
  );
}
